import { useEffect, useState } from 'react';
import { Box, IconButton, SxProps, Theme, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { ServiceItem } from 'orderme-api-integration-client';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { roundDoubleNumbers } from '../../helpers/currency';
import { addToCartAsync, removeOneFromCartAsync } from '../../redux/reducers/shoppingCartReducer';
import { RootState } from '../../redux/store';

interface AddToCartActionBoxProps {
  service: ServiceItem;
  visible: boolean;
  isSticky?: boolean;
}

export function AddToCartActionBox(props: AddToCartActionBoxProps) {
  const { service, visible, isSticky = false } = props;
  const dispatch = useAppDispatch();
  const shoppingCartItems = useAppSelector((state: RootState) => state.shoppingCartState.items);
  const updatingCart = useAppSelector((state: RootState) => state.shoppingCartState.updatingCart);
  const [servicesCount, setServicesCount] = useState<number>(0);
  const [canAdd, setCanAdd] = useState<boolean>(false);
  const [canRemove, setCanRemove] = useState<boolean>(false);

  useEffect(() => {
    setCanAdd(!updatingCart);
    setCanRemove(servicesCount > 0 && !updatingCart);
  }, [updatingCart, servicesCount]);

  useEffect(() => {
    const cartItem = shoppingCartItems.find((i) => i.serviceId === service.id);
    setServicesCount(cartItem?.count ?? 0);
  }, [shoppingCartItems, service]);

  const addProductToCart = (service: ServiceItem) => {
    dispatch(addToCartAsync(service.id));
  };

  const removeFromCart = (service: ServiceItem) => {
    dispatch(removeOneFromCartAsync(service.id));
  };

  const iconSx = (isDisabled: boolean) =>
    ({
      height: '40px',
      width: '40px',
      color: isDisabled ? 'grey' : 'orange',
    }) as SxProps<Theme>;

  return (
    <Box
      sx={{
        display: !visible && 'none',
        position: isSticky ? 'sticky' : 'static',
        bottom: 0,
        zIndex: 1,
        backgroundColor: 'white',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Typography
          sx={{
            fontSize: '28px',
            opacity: 0.7,
            textDecoration: 'line-through',
            pr: '15px',
            display: service.originalPrice - service.price === 0 ? 'none' : 'inline-block',
          }}
        >
          {roundDoubleNumbers(service.originalPrice)} €
        </Typography>
        <Typography sx={{ fontSize: '28px', fontWeight: 'bold' }}> {roundDoubleNumbers(service.price)} €</Typography>
      </Box>
      <Box sx={{ width: '200px', mx: 'auto', display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }}>
        <IconButton disabled={!canRemove} onClick={() => removeFromCart(service)}>
          <RemoveCircleOutlineIcon sx={iconSx(!canRemove)} />
        </IconButton>
        <Typography sx={{ marginTop: '15px', fontWeight: 'bold', fontSize: '25px' }}>{servicesCount}</Typography>
        <IconButton disabled={!canAdd} onClick={() => addProductToCart(service)}>
          <AddCircleOutlineIcon sx={iconSx(!canAdd)} />
        </IconButton>
      </Box>
    </Box>
  );
}
